import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import {
  AlertTriangle,
  ArrowRight,
  Award,
  Briefcase,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  FileHeart,
  FlaskConical,
  GraduationCap,
  Layers,
  Menu,
  MessageSquare,
  Microscope,
  RotateCcw,
  Shield,
  Stethoscope,
  X,
} from 'lucide-react';

/* ── All 12 modules for the mega menu ────────────────────────────── */

const MODULE_GROUPS = [
  {
    heading: 'Academic Operations',
    items: [
      { icon: CalendarDays, label: 'Attendance & Sessions', slug: 'attendance', desc: 'Session management and attendance tracking' },
      { icon: GraduationCap, label: 'Internal Assessments', slug: 'ia-tracker', desc: 'Category-aware IA marks and eligibility' },
      { icon: Layers, label: 'Elective Management', slug: 'electives', desc: 'Events, registration, scheduling, and marks' },
    ],
  },
  {
    heading: 'Clinical & Rotations',
    items: [
      { icon: Stethoscope, label: 'Clinical Postings', slug: 'clinical-postings', desc: 'Auto-scheduled rotation postings' },
      { icon: RotateCcw, label: 'CRMI Rotations', slug: 'crmi', desc: 'Internship scheduling and attendance' },
      { icon: Microscope, label: 'PG Resident Tracking', slug: 'pg-residents', desc: 'Logbook, thesis milestones, and guides' },
    ],
  },
  {
    heading: 'Faculty & Feedback',
    items: [
      { icon: Briefcase, label: 'Faculty Management', slug: 'faculty', desc: 'Sessions, leave, conferences, and reports' },
      { icon: FlaskConical, label: 'Research Activities', slug: 'research', desc: 'Department research register with IEC tracking' },
      { icon: MessageSquare, label: 'Feedback & Surveys', slug: 'feedback', desc: 'Anonymous feedback with threshold protection' },
    ],
  },
  {
    heading: 'Student Support',
    items: [
      { icon: FileHeart, label: 'Wellness & Discipline', slug: 'wellness', desc: 'Mentorship, counseling, and compliance' },
      { icon: AlertTriangle, label: 'Early Warning', slug: 'early-warning', desc: 'Risk scoring across all data sources' },
      { icon: Award, label: 'Outreach & Activities', slug: 'outreach', desc: 'Extracurricular and community activities' },
    ],
  },
];

/* Top-level nav mirrors the 4 mega-menu groups */
const TOP_NAV_ITEMS = [
  { label: 'Academic Operations', firstSlug: 'attendance' },
  { label: 'Clinical & Rotations', firstSlug: 'clinical-postings' },
  { label: 'Faculty & Feedback', firstSlug: 'faculty' },
  { label: 'Student Support', firstSlug: 'wellness' },
];

const ease = [0.21, 0.47, 0.32, 0.98];

export default function PublicNav() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const megaRef = useRef(null);
  const triggerRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (megaOpen && megaRef.current && !megaRef.current.contains(e.target) && !triggerRef.current.contains(e.target)) {
        setMegaOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [megaOpen]);

  const openMega = () => { clearTimeout(closeTimer.current); setMegaOpen(true); };
  const scheduleMegaClose = () => { closeTimer.current = setTimeout(() => setMegaOpen(false), 200); };
  const cancelMegaClose = () => { clearTimeout(closeTimer.current); };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'border-b border-stone-200/70 bg-white/85 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
            : 'bg-white/60 backdrop-blur-md'
        }`}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity shrink-0">
            <motion.div
              className="w-9 h-9 rounded-xl bg-[#1F3C2F] flex items-center justify-center shadow-sm"
              whileHover={{ scale: 1.05, rotate: 3 }}
              whileTap={{ scale: 0.95 }}
            >
              <Shield className="w-4 h-4 text-white" />
            </motion.div>
            <div className="hidden sm:block">
              <p className="text-base font-semibold tracking-tight text-[#1A1C1A]" style={{ fontFamily: 'Outfit' }}>CliniQ 360</p>
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#7A7F7A]">Operations Platform</p>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Modules mega-menu trigger */}
            <div className="relative" ref={triggerRef}>
              <button
                className={`inline-flex items-center gap-1 text-sm px-3 py-2 rounded-lg transition-colors ${
                  megaOpen ? 'text-[#1F3C2F] bg-[#1F3C2F]/5' : 'text-[#5C605C] hover:text-[#1A1C1A] hover:bg-stone-100/60'
                }`}
                onMouseEnter={openMega}
                onMouseLeave={scheduleMegaClose}
                onClick={() => setMegaOpen(!megaOpen)}
              >
                Modules
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${megaOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {TOP_NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={`/features/${item.firstSlug}`}
                className="text-sm text-[#5C605C] hover:text-[#1A1C1A] transition-colors px-3 py-2 rounded-lg hover:bg-stone-100/60"
              >
                {item.label}
              </Link>
            ))}

            <div className="ml-4">
              <Button
                data-testid="nav-login-btn"
                className="bg-[#1F3C2F] text-white hover:bg-[#325744] h-9 px-5 text-sm shadow-[0_1px_2px_rgba(31,60,47,0.3),inset_0_1px_0_rgba(255,255,255,0.08)]"
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
            </div>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-stone-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </motion.nav>

      {/* ── Desktop Mega Menu ──────────────────────────────────────── */}
      <AnimatePresence>
        {megaOpen && (
          <motion.div
            ref={megaRef}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease }}
            className="fixed top-16 left-0 right-0 z-40 hidden lg:block"
            onMouseEnter={cancelMegaClose}
            onMouseLeave={scheduleMegaClose}
          >
            <div className="max-w-7xl mx-auto px-6 pt-2">
              <div className="rounded-2xl border border-stone-200/80 bg-white/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.04)] p-6">
                <div className="grid grid-cols-4 gap-6">
                  {MODULE_GROUPS.map((group) => (
                    <div key={group.heading}>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-semibold mb-3 px-1">{group.heading}</p>
                      <div className="space-y-0.5">
                        {group.items.map((item) => (
                          <Link
                            key={item.slug}
                            to={`/features/${item.slug}`}
                            className="group flex items-start gap-3 p-2.5 rounded-xl hover:bg-[#1F3C2F]/[0.04] transition-colors"
                            onClick={() => setMegaOpen(false)}
                          >
                            <div className="w-8 h-8 rounded-lg bg-[#1F3C2F]/8 text-[#1F3C2F] flex items-center justify-center shrink-0 group-hover:bg-[#1F3C2F]/12 transition-colors mt-0.5">
                              <item.icon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#1A1C1A] group-hover:text-[#1F3C2F] transition-colors leading-tight">{item.label}</p>
                              <p className="text-xs text-stone-400 mt-0.5 leading-snug">{item.desc}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom bar */}
                <div className="mt-5 pt-4 border-t border-stone-100 flex items-center justify-between">
                  <p className="text-xs text-stone-400">12 modules — one coherent operating layer for your institution</p>
                  <Link
                    to="/"
                    onClick={(e) => {
                      setMegaOpen(false);
                      if (window.location.pathname === '/') {
                        e.preventDefault();
                        document.getElementById('modules')?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1F3C2F] hover:underline"
                  >
                    View all modules <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Mobile Menu ────────────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-16 bottom-0 z-40 bg-white/98 backdrop-blur-xl overflow-y-auto lg:hidden"
          >
            <div className="px-6 py-5 space-y-6">
              {/* Module groups */}
              {MODULE_GROUPS.map((group) => (
                <div key={group.heading}>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-semibold mb-2 px-3">{group.heading}</p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/features/${item.slug}`}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 transition-colors"
                        onClick={() => setMobileOpen(false)}
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#1F3C2F]/8 text-[#1F3C2F] flex items-center justify-center shrink-0">
                          <item.icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1A1C1A]">{item.label}</p>
                          <p className="text-xs text-stone-400">{item.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}

              <Button
                data-testid="nav-mobile-login-btn"
                className="w-full bg-[#1F3C2F] hover:bg-[#325744] text-white h-11"
                onClick={() => { navigate('/login'); setMobileOpen(false); }}
              >
                Sign In
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
